// src/pages/VendorDetail.js
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Cache from "../utils/cache";
import {
    getDocs,
    collection,
} from "firebase/firestore";
import { db } from "../firebase";
import { normalizeVendorName, isVendorMatch } from "../utils/vendorMatchUtils";

const WEEKLY_ADS_PAGE_SIZE = 8;

// Normalize function to simplify vendor name comparisons
function normalize(str) {
    return (str || "")
        .toLowerCase()
        .replace(/^the\s+/g, "")
        .replace(/\.com$/, "")        // remove trailing .com
        .replace(/&/g, "and")         // replace & with "and"
        .replace(/['’]/g, "")         // remove apostrophes
        .replace(/\s+/g, "")          // remove whitespace
        .replace(/[^a-z0-9]/g, "")    // remove other non-alphanumerics
        .replace(/['’‘`]/g, "")
        .replace(/[-.]?(com|net|org|co|io|us|biz|info|online|store|shop|site|app)$/, "") // domain suffixes
        .replace(/(inc|llc|ltd|corp|corporation|company|airways|store|the)$/, "") // business suffixes
        .trim();
}


export default function VendorDetail() {

    const [loading, setLoading] = useState(true);
    const { vendorId } = useParams();
    const navigate = useNavigate();

    const [vendorLogo, setVendorLogo] = useState("");
    const [vendorAddress, setVendorAddress] = useState("");
    const [localProductOffers, setLocalProductOffers] = useState([]); // safe local copy of product offers
    const [websiteDeals, setWebsiteDeals] = useState([]);
    const [weeklyAds, setWeeklyAds] = useState([]);
    const [weeklyAdsVendorsLogos, setWeeklyAdsVendorsLogos] = useState({});
    const [weeklyAdsPage, setWeeklyAdsPage] = useState(1);
    const [groupedBankOffers, setGroupedBankOffers] = useState({});
    const [banksMeta, setBanksMeta] = useState({});

    const [showPopup, setShowPopup] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    const containsWine = useCallback((text) => text?.toLowerCase().includes("wine"), []);

    function formatDate(timestamp) {
        if (!timestamp) return null;
        if (timestamp.toDate) return timestamp.toDate().toLocaleDateString();
        if (timestamp instanceof Date) return timestamp.toLocaleDateString();
        return new Date(timestamp).toLocaleDateString();
    }

    useEffect(() => {
        const vendorIdLower = vendorId.toLowerCase();

        async function loadData() {
            setLoading(true);  // start loading
            let {
                vendors,
                productOffers,
                bankOffers,
                banksMeta: cachedBanksMeta,
                websiteOffers,
                weeklyAds: cachedWeeklyAds,
            } = Cache.getData() || {};

            // Fetch vendors if not cached
            if (!vendors?.length || typeof vendors[0] !== "object") {
                const vendorsSnap = await getDocs(collection(db, "vendors"));
                vendors = [];
                vendorsSnap.docs.forEach((doc) => {
                    const data = doc.data();
                    if (Array.isArray(data.vendors)) {
                        vendors.push(
                            ...data.vendors.map((v) => ({
                                ...v,
                                vendorName: v.vendorName || v.id,
                            }))
                        );
                    }
                });
            }

            // Fetch product offers if not cached properly
            const productOffersCacheValid = Array.isArray(productOffers.offers) && productOffers.offers.length > 0;
            if (!productOffersCacheValid) {
                const productSnap = await getDocs(collection(db, "product_offers"));
                let allOffers = [];
                productSnap.forEach((doc) => {
                    const data = doc.data();
                    if (Array.isArray(data.offers)) {
                        allOffers = allOffers.concat(data.offers.map(offer => ({
                            ...offer,
                            // add doc id + index for unique id to track likes/votes etc
                            id: `${doc.id}_${offer.offerId || Math.random().toString(36).substring(2, 8)}`,
                            uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
                        })));
                    }
                });

                const nowUTC = new Date();

                productOffers = allOffers.filter((offer) => {
                    // Convert Firestore Timestamp to JS Date, fallback to Date if needed
                    const endDate = offer.endDttm?.toDate ? offer.endDttm.toDate() : offer.endDttm ? new Date(offer.endDttm) : null;
                    const startDate = offer.startDttm?.toDate ? offer.startDttm.toDate() : offer.startDttm ? new Date(offer.startDttm) : null;

                    return (
                        (!endDate || endDate >= nowUTC) &&  // not expired
                        (!startDate || startDate <= nowUTC) &&  // already started
                        !containsWine(offer.vendor) &&
                        !containsWine(offer.description)
                    );
                });
            }

            // Fetch website offers if not cached
            if (!websiteOffers?.length) {
                const websiteSnap = await getDocs(collection(db, "website_offers"));
                websiteOffers = [];
                websiteSnap.docs.forEach((doc) => {
                    const data = doc.data();
                    if (Array.isArray(data.offers)) {
                        data.offers.forEach((offer) => {
                            websiteOffers.push({
                                ...offer,
                                offerId: offer.offerId || doc.id,
                            });
                        });
                    }
                });
            }

            // Fetch weekly ads if not cached
            if (!cachedWeeklyAds?.length) {
                const weeklyAdsSnap = await getDocs(collection(db, "weekly_ads"));
                cachedWeeklyAds = weeklyAdsSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
            }

            // Fetch bank offers if not cached
            if (!bankOffers || Object.keys(bankOffers).length === 0) {
                const bankOffersSnap = await getDocs(collection(db, "bank_offers"));
                bankOffers = [];
                bankOffersSnap.forEach((doc) => {
                    const data = doc.data();
                    if (Array.isArray(data.offers)) {
                        data.offers.forEach((offer) => {
                            bankOffers.push({ ...offer, bankName: doc.id });
                        });
                    }
                });
            }

            // Fetch bank metadata if not cached
            if (!cachedBanksMeta || Object.keys(cachedBanksMeta).length === 0) {
                const banksSnap = await getDocs(collection(db, "banks"));
                cachedBanksMeta = {};

                banksSnap.forEach((doc) => {
                    const bankName = doc.id.toLowerCase();
                    cachedBanksMeta[bankName] = {
                        logo: doc.data().logo || "",
                        address: doc.data().address || "",
                    };
                });
            }

            // Save updated cache
            Cache.setData({
                vendors,
                productOffers,
                bankOffers,
                websiteOffers,
                weeklyAds: cachedWeeklyAds,
                banksMeta: cachedBanksMeta,
            });

            // --- All code below stays unchanged ---
            const vendor = vendors.find(
                (v) => (v.vendorName || "").toLowerCase() === vendorIdLower
            );

            setVendorLogo(vendor?.logo || "");
            setVendorAddress(vendor?.address || "");

            const localCopy = (productOffers || []).map((doc) => ({
                ...doc,
                offers: Array.isArray(doc.offers) ? [...doc.offers] : [],
            }));
            setLocalProductOffers(localCopy);

            setWebsiteDeals(
                websiteOffers.filter(
                    (offer) => (offer.vendor || "").toLowerCase() === vendorIdLower
                )
            );

            const now = new Date();
            const filteredWeeklyAds = cachedWeeklyAds.filter((ad) => {
                if (!ad.vendor) return false;
                if (ad.vendor.toLowerCase() !== vendorIdLower) return false;
                if (!ad.startDate || !ad.endDate) return true;
                return ad.startDate.toDate() <= now && ad.endDate.toDate() >= now;
            });
            setWeeklyAds(filteredWeeklyAds);

            let logos = {};
            if (vendor) logos[vendor.vendorName] = vendor.logo || null;
            setWeeklyAdsVendorsLogos(logos);

            const grouped = {};
            bankOffers.forEach((offer) => {
                const bankVendorNorm = normalizeVendorName(offer.vendor || offer.bankName);
                const normalizedVendorId = normalizeVendorName(vendorId);

                if (!grouped[bankVendorNorm]) grouped[bankVendorNorm] = [];
                grouped[bankVendorNorm].push(offer);

                if (isVendorMatch(bankVendorNorm, normalizedVendorId)) {
                    if (!grouped[normalizedVendorId]) grouped[normalizedVendorId] = [];
                    grouped[normalizedVendorId].push(offer);
                }
            });

            Object.keys(grouped).forEach((key) => {
                const seen = new Set();
                grouped[key] = grouped[key].filter((offer) => {
                    const hash = `${offer.vendor}|${offer.offer || offer.description || ""}`;
                    if (seen.has(hash)) return false;
                    seen.add(hash);
                    return true;
                });
            });

            setGroupedBankOffers(grouped);
            setBanksMeta(cachedBanksMeta);
            setLoading(false);  // finished loading

            const savedState = JSON.parse(sessionStorage.getItem("vendorDetailState") || "{}");
            if (savedState) {
                if (savedState.showAllOffers !== undefined) setShowAllOffers(savedState.showAllOffers);
                if (savedState.weeklyAdsPage !== undefined) setWeeklyAdsPage(savedState.weeklyAdsPage);

                setTimeout(() => {
                    window.scrollTo(0, savedState.scrollPosition || 0);
                }, 100);
            }

        }


        loadData();

    }, [vendorId, containsWine]);

    const sectionTitleClass = "text-2xl font-semibold mb-3 text-indigo-700";

    const normalizedVendorId = normalize(vendorId);
    const filteredProductOffers = (localProductOffers || []).filter(
        offer => normalize(offer.vendor) === normalizedVendorId
    );

    const [showAllOffers, setShowAllOffers] = useState(false);

    const displayedProductOffers = showAllOffers
        ? filteredProductOffers
        : filteredProductOffers.slice(0, 5);

    const hasProductOffers = filteredProductOffers.length > 0;
    const hasWebsiteDeals = websiteDeals.length > 0;
    const hasWeeklyAds = weeklyAds.length > 0;
    const hasBankOffers = Boolean(
        groupedBankOffers[normalizedVendorId]?.length
    );

    const noOffersFound =
        !hasProductOffers && !hasWebsiteDeals && !hasWeeklyAds && !hasBankOffers;

    const weeklyAdsPageSlice = weeklyAds.slice(
        (weeklyAdsPage - 1) * WEEKLY_ADS_PAGE_SIZE,
        weeklyAdsPage * WEEKLY_ADS_PAGE_SIZE
    );

    const handleRebateClick = (product) => {
        setSelectedProduct(product);
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
        setSelectedProduct(null);
    };

    const totalWeeklyAdsPages = Math.ceil(weeklyAds.length / WEEKLY_ADS_PAGE_SIZE);

    const handleProductClick = (offer) => {
        const scrollPosition = window.scrollY;
        const vendorDetailState = {
            scrollPosition,
            showAllOffers,
            weeklyAdsPage,
        };
        sessionStorage.setItem("vendorDetailState", JSON.stringify(vendorDetailState));
        navigate(`/product/${offer.id || offer.offerId}`);
    };

    return (
        <div className="max-w-6xl mx-auto px-4 pb-12">
            <div className="flex justify-between items-center mb-6">
                <button
                    onClick={() => {
                        sessionStorage.removeItem("vendorDetailState");
                        navigate("/vendors");
                    }}
                    className="text-sm bg-gray-200 px-4 py-2 rounded hover:bg-gray-300 transition"
                >
                    ← Back to Vendors
                </button>

                {vendorLogo ? (
                    <a
                        href={vendorAddress || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={vendorId}
                    >
                        <img
                            src={vendorLogo}
                            alt={vendorId}
                            className="h-12 sm:h-16 object-contain cursor-pointer"
                        />
                    </a>
                ) : (
                    <a
                        href={vendorAddress || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={vendorId}
                        className="font-bold text-gray-900 select-none cursor-pointer"
                        style={{
                            fontSize: "clamp(1.5rem, 5vw, 3rem)",
                            lineHeight: 1.1,
                            maxWidth: "200px",
                            textAlign: "right",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                        }}
                    >
                        {vendorId}
                    </a>
                )}
            </div>

            {loading && (
                <p className="text-center text-gray-500 italic mt-20 text-lg">Loading offers...</p>
            )}

            {!loading && noOffersFound && (
                <p className="text-center text-gray-500 italic mt-20 text-lg">No offers found. Please check back later.</p>
            )}


            {hasProductOffers && (
                <section>
                    <h2 className={sectionTitleClass}>Product Offers</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-6">
                        {displayedProductOffers.map((offer) => (
                            <div
                                key={offer.id || offer.offerId}
                                className="relative border rounded-xl p-4 shadow hover:shadow-lg transition bg-white flex flex-col justify-between cursor-pointer"
                                onClick={() => handleProductClick(offer)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleProductClick(offer);
                                    }
                                }}
                                aria-label={`View details for ${offer.productName}`}
                            >
                                {offer.rebateAmount > 0 && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRebateClick(offer);
                                        }}
                                        className="absolute top-2 right-2 bg-red-100 px-2 py-1 rounded text-red-700 font-bold animate-pulse text-xs shadow"
                                        type="button"
                                    >
                                        Rebate Offer
                                    </button>
                                )}

                                <div>
                                    <img
                                        src={offer.productImage || offer.offerImage || ""}
                                        alt={offer.productName}
                                        className="h-40 w-full object-contain mb-2"
                                    />
                                    <h2 className="text-md font-bold">{offer.productName}</h2>
                                    <p className="text-sm text-gray-600">{offer.description}</p>

                                    <div className="mt-1 text-sm">
                                        {!isNaN(Number(offer.offerPrice)) && offer.offerPrice !== "" && (
                                            <p className="mb-0.5 text-indigo-600 font-bold text-base sm:text-lg">
                                                ${Number(offer.offerPrice).toFixed(2)}
                                                {!isNaN(Number(offer.originalPrice)) && offer.originalPrice !== "" && (
                                                    <span className="ml-2 line-through text-gray-500 text-sm sm:text-base font-semibold">
                                                        ${Number(offer.originalPrice).toFixed(2)}
                                                    </span>
                                                )}
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-2 text-xs mt-1">
                                        {(offer.category || (Array.isArray(offer.tags) && offer.tags.length > 0)) && (
                                            <p className="mb-1 text-xs text-gray-600">
                                                {offer.category && <>Category: {offer.category}<br /></>}
                                            </p>
                                        )}
                                        {offer.offerType && (
                                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{offer.offerType}</span>
                                        )}
                                        {offer.freeStorePickup?.toLowerCase() === "yes" && (
                                            <p className="mb-1 text-green-600 font-semibold text-xs">🚚 Free Ship to Store</p>
                                        )}
                                    </div>
                                </div>
                                {/* Removed Buy Now button */}
                            </div>
                        ))}
                    </div>

                    {filteredProductOffers.length > 5 && (
                        <div className="flex justify-center mt-4">
                            <button
                                onClick={() => setShowAllOffers(!showAllOffers)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1 rounded shadow"
                                type="button"
                            >
                                {showAllOffers ? "Show Less" : `Show All (${filteredProductOffers.length})`}
                            </button>
                        </div>
                    )}

                    <hr className="my-6 border-gray-300" />
                </section>
            )}

            {hasWebsiteDeals && (
                <section>
                    <h2 className={sectionTitleClass}>Website Deals</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                        {websiteDeals.map((offer, i) => (
                            <a
                                key={i}
                                href={vendorAddress || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-white shadow p-4 rounded hover:shadow-lg transition block"
                            >
                                <div className="text-sm">{offer.description}</div>
                                {offer.promoCode && (
                                    <div className="text-purple-600 text-sm mt-1">
                                        Code: {offer.promoCode}
                                    </div>
                                )}
                                {formatDate(offer.offerEndDttm) && (
                                    <div className="text-xs text-gray-500 mt-1">
                                        Ends on: {formatDate(offer.offerEndDttm)}
                                    </div>
                                )}
                            </a>
                        ))}
                    </div>
                    <hr className="my-6 border-gray-300" />
                </section>
            )}

            {hasWeeklyAds && (
                <section>
                    <h2 className={sectionTitleClass}>Weekly Ads</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 mb-6">
                        {weeklyAdsPageSlice.map((ad) => {
                            const logo = weeklyAdsVendorsLogos[ad.vendor] || null;
                            return (
                                <a
                                    key={ad.id}
                                    href={ad.offerLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border rounded-lg p-4 shadow hover:shadow-lg transition flex flex-col items-center cursor-pointer"
                                    title={`${ad.vendor} Weekly Ad`}
                                >
                                    <div
                                        className="w-24 h-24 flex items-center justify-center rounded mb-2 p-2"
                                        style={{
                                            background: "rgba(200, 200, 200, 0.85)",
                                            boxShadow: "0 0 8px rgba(0,0,0,0.25)",
                                            border: "1px solid #999",
                                        }}
                                    >
                                        {logo ? (
                                            <img
                                                src={logo}
                                                alt={`${ad.vendor} logo`}
                                                className="max-w-full max-h-full object-contain drop-shadow-md"
                                                style={{ filter: "drop-shadow(0 0 1px rgba(0,0,0,0.2))" }}
                                            />
                                        ) : (
                                            <div className="text-xs text-gray-400 text-center">No logo</div>
                                        )}
                                    </div>
                                    <div className="text-center font-semibold text-sm truncate max-w-full">
                                        {ad.vendor}
                                    </div>
                                    {ad.description && (
                                        <div className="mt-1 text-xs font-bold text-teal-600 text-center max-w-full">
                                            {ad.description}
                                        </div>
                                    )}
                                    {formatDate(ad.endDate) && (
                                        <div className="text-xs text-gray-500 mt-1">
                                            Ends on: {formatDate(ad.endDate)}
                                        </div>
                                    )}
                                </a>
                            );
                        })}
                    </div>

                    {totalWeeklyAdsPages > 1 && (
                        <div className="mt-6 flex justify-center space-x-4">
                            <button
                                onClick={() => setWeeklyAdsPage((p) => Math.max(1, p - 1))}
                                disabled={weeklyAdsPage === 1}
                                className="px-3 py-1 rounded border disabled:opacity-50"
                            >
                                Prev
                            </button>
                            <span className="px-3 py-1 rounded border bg-gray-100">
                                Page {weeklyAdsPage} of {totalWeeklyAdsPages}
                            </span>
                            <button
                                onClick={() => setWeeklyAdsPage((p) => Math.min(totalWeeklyAdsPages, p + 1))}
                                disabled={weeklyAdsPage === totalWeeklyAdsPages}
                                className="px-3 py-1 rounded border disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                    <hr className="my-6 border-gray-300" />
                </section>
            )}

            {hasBankOffers && (
                <section>
                    <h2 className={sectionTitleClass}>Bank Offers</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-6">
                        {groupedBankOffers[normalizedVendorId].map((offer, i) => {
                            const bankMeta = banksMeta[offer.bankName.toLowerCase()] || {};
                            const logo = bankMeta.logo || "";
                            const address = bankMeta.address || "#";

                            return (
                                <a
                                    key={offer.id || i}
                                    href={address}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="border rounded-lg p-4 shadow hover:shadow-lg transition flex flex-col cursor-pointer items-center text-center"
                                    title={`${offer.bankName} Bank Offer`}
                                >
                                    {logo && (
                                        <img
                                            src={logo}
                                            alt={`${offer.bankName} logo`}
                                            className="h-10 mb-3 object-contain"
                                        />
                                    )}
                                    <div className="text-lg font-semibold mb-2">{offer.vendor && offer.vendor.includes("##") ? offer.category : offer.vendor}</div>
                                    <div className="text-sm">{offer.offer || offer.description}</div>
                                </a>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Popup for rebate */}
            {showPopup && selectedProduct && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
                    onClick={closePopup}
                >
                    <div
                        className="bg-white p-6 rounded shadow-lg max-w-lg w-full relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute top-3 right-3 text-gray-700 hover:text-gray-900"
                            onClick={closePopup}
                            aria-label="Close popup"
                        >
                            ✕
                        </button>
                        <h3 className="text-xl font-bold mb-3">{selectedProduct.productName}</h3>
                        <p>{selectedProduct.description}</p>
                        <p className="mt-2">
                            Rebate Amount:{" "}
                            <span className="font-semibold text-green-700">
                                ${selectedProduct.rebateAmount.toFixed(2)}
                            </span>
                        </p>
                        <a
                            href={selectedProduct.offerLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-block bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
                        >
                            Redeem Rebate
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}
